import styled, { css } from 'styled-components'
import { Link } from 'react-router-dom'

import Container from 'src/components/Container'
import { LinkCardWrapper, ItemLink, ItemSubtitle, TitleWrapper } from 'src/components/LinkCard/styled'
import BuildingPhoto from 'url:./assets/building-photo.jpg'
import PinImage from 'url:./assets/pin.svg'

export const SectionHeading = styled.h2`
	font-size: 33px;
	font-weight: 700;
	line-height: normal;
	color: ${({ theme }) => theme.colors.primary};
`

const outlineButtonStyles = css`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	box-sizing: border-box;
	height: 45px;
	background: ${({ theme }) => theme.colors.white};
	border: 2px solid ${({ theme }) => theme.colors.primary};
	border-radius: 6px;
	padding: 0 15px;
	font-size: 19px;
	font-weight: 700;
	line-height: 1;
	color: ${({ theme }) => theme.colors.primary};
	text-decoration: none;
	text-align: center;
	cursor: pointer;
	&:hover {
		background: ${({ theme }) => theme.colors.lightestBackground};
	}
`

export const OutlineLink = styled(Link)`
	${outlineButtonStyles}
`

export const OutlineButton = styled.button`
	${outlineButtonStyles}
	margin: 0;
`

/* Hero news carousel: full-bleed 500px band directly under the nav */
export const HeroWrapper = styled.div`
	position: relative;
	width: 100%;
	height: 500px;
	overflow: hidden;
`

export const HeroViewport = styled.div`
	overflow: hidden;
	height: 100%;
`

export const HeroSlides = styled.div`
	display: flex;
	height: 100%;
`

export const HeroSlide = styled.div<{ $imageUrl?: string }>`
	flex: 0 0 100%;
	min-width: 0;
	height: 100%;
	position: relative;
	background-color: ${({ theme }) => theme.colors.primaryTextColor};
	background-image: ${({ $imageUrl }) => ($imageUrl ? `url(${$imageUrl})` : 'none')};
	background-size: cover;
	background-position: center;
	&:before {
		content: '';
		position: absolute;
		inset: 0;
		background: rgba(0, 0, 0, 0.1);
	}
`

export const HeroOverlayContainer = styled(Container)<{ $controls?: boolean }>`
	position: ${({ $controls }) => ($controls ? 'absolute' : 'relative')};
	${({ $controls }) =>
		$controls &&
		`
		left: 0;
		right: 0;
		bottom: 50px;
		pointer-events: none;
		> * {
			pointer-events: auto;
		}
	`}
	height: ${({ $controls }) => ($controls ? 'auto' : '100%')};
`

export const HeroTitleBox = styled.div`
	position: absolute;
	top: 65px;
	left: var(--grid-basis);
	width: 600px;
	height: 320px;
	box-sizing: border-box;
	padding: 40px 39px;
	background: rgba(51, 51, 51, 0.8);
	color: ${({ theme }) => theme.colors.white};
	display: flex;
	flex-direction: column;
	align-items: flex-start;
`

export const HeroDepartment = styled.div`
	font-size: 18px;
	font-weight: 700;
	line-height: 22px;
	text-transform: uppercase;
`

export const HeroTitle = styled.h1`
	font-size: 35px;
	font-weight: 700;
	line-height: 39px;
	width: 530px;
	margin-top: 20px;
	max-height: 78px;
	overflow: hidden;
`

export const HeroMeta = styled.div`
	font-size: 18px;
	font-weight: 700;
	line-height: 20px;
	margin-top: 8px;
	white-space: nowrap;
`

export const HeroTitleBoxLink = styled(OutlineLink)`
	margin-top: auto;
`

export const HeroArrow = styled.button<{ $side: 'left' | 'right' }>`
	position: absolute;
	bottom: 0;
	${({ $side }) =>
		$side === 'left' ? 'left: -5px;' : 'left: calc(var(--grid-basis) + 621px);'}
	width: 10px;
	height: 25px;
	padding: 0;
	border: 0;
	background: none;
	cursor: pointer;
	transform: translateY(9px);
	img {
		width: 10px;
		height: 25px;
		transform: ${({ $side }) => ($side === 'left' ? 'scaleX(-1)' : 'none')};
	}
`

export const HeroBars = styled.div`
	position: absolute;
	bottom: 0;
	left: calc(var(--grid-basis) + 3px);
	width: 600px;
	display: flex;
	gap: 20px;
	height: 7px;
	align-items: center;
`

export const HeroBar = styled.button<{ $active: boolean }>`
	flex: 1 0 0;
	min-width: 1px;
	height: 5px;
	padding: 0;
	border: 0;
	cursor: pointer;
	background: ${({ $active, theme }) => ($active ? theme.colors.primary : 'rgba(255, 255, 255, 0.9)')};
`

export const MoreNewsLink = styled(Link)`
	position: absolute;
	right: 0;
	bottom: 41px;
	width: 192px;
	height: 48px;
	box-sizing: border-box;
	padding: 0 18px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	background: rgba(51, 51, 51, 0.8);
	color: ${({ theme }) => theme.colors.white};
	font-size: 20px;
	font-weight: 700;
	text-decoration: none;
	img {
		width: 12px;
		height: 22px;
	}
	&:hover {
		background: rgba(51, 51, 51, 0.95);
	}
`

/* Cheers! box (780 wide, #F6F6F6) beside the 360-wide side column, 49px under the hero */
export const HomeColumns = styled.div`
	display: flex;
	gap: 40px;
	margin-top: 49px;
`

export const HomeSideColumn = styled.div`
	flex: 1 1 0;
	min-width: 0;
	display: flex;
	flex-direction: column;
	padding-bottom: 47px;
`

export const CheersBox = styled.div`
	position: relative;
	flex: 0 0 780px;
	max-width: 780px;
	box-sizing: border-box;
	background: ${({ theme }) => theme.colors.lightestBackground};
	padding: 23px 36px 47px;
`

export const CheersLogo = styled.img`
	display: block;
	width: 166px;
	height: auto;
`

export const CheersSubhead = styled.p`
	width: 478px;
	margin: 14px 0 0;
	font-size: 31px;
	line-height: 37px;
`

/* The Figma places the button over the subhead's text box (they overlap by 9px), so it is positioned, not flowed */
export const CheersNominateButton = styled(OutlineButton)`
	position: absolute;
	top: 81px;
	right: 63px;
	white-space: nowrap;
`

export const RecentNominationsHeading = styled.h3`
	margin-top: 40px;
	font-size: 22px;
	font-weight: 700;
	line-height: normal;
	color: ${({ theme }) => theme.colors.primary};
`

export const NominationsGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	column-gap: 32px;
	margin-top: 21px;
`

/* Resources / Favorites column: LinkCard rows restyled to the facelift (39×41 icon, 18/24 title, 15/24 meta) */
export const ResourceRows = styled.div`
	display: flex;
	flex-direction: column;
	gap: 36px;
	margin-top: 24px;
	${LinkCardWrapper} {
		padding: 0;
		border-bottom: none;
		align-items: flex-start;
		> img,
		> svg {
			margin-right: 12px;
			align-self: flex-start;
		}
	}
	${TitleWrapper} {
		line-height: 24px;
	}
	${ItemLink} {
		padding-bottom: 0;
		font-size: 18px;
		line-height: 24px;
	}
	${ItemSubtitle} {
		font-size: 15px;
		line-height: 24px;
		color: ${({ theme }) => theme.colors.mutedText};
	}
`

export const FavoritesHeading = styled.h3`
	display: flex;
	align-items: center;
	margin-top: 59px;
	font-size: 24px;
	font-weight: 700;
	line-height: normal;
	color: ${({ theme }) => theme.colors.primary};
`

export const FavoritesPin = styled.img.attrs({ src: PinImage, alt: '' })`
	width: 24px;
	height: 43px;
	margin-left: 4px;
	margin-right: 18px;
	transform: rotate(30deg);
`

export const FavoriteRows = styled(ResourceRows)`
	margin-top: 17px;
`

export const NoFavoritesText = styled.p`
	margin: 25px 0 0;
	font-size: 15px;
	line-height: 24px;
	color: ${({ theme }) => theme.colors.mutedText};
`

export const MoreResourcesLink = styled(Link)`
	align-self: flex-start;
	margin-top: auto;
	padding-top: 40px;
	font-size: 18px;
	line-height: 22px;
	color: ${({ theme }) => theme.colors.primary};
	text-decoration: none;
`

/* Frequently Accessed Links: 8 tiles 130×130, 2px medium-gray border, 20px gaps (= 1180 content width) */
export const FrequentlyAccessedHeading = styled(SectionHeading)`
	margin-top: 44px;
`

export const TileRow = styled.div`
	display: flex;
	gap: 20px;
	margin-top: 14px;
`

export const Tile = styled.a`
	display: flex;
	flex-direction: column;
	align-items: center;
	box-sizing: border-box;
	width: 130px;
	height: 130px;
	border: 2px solid ${({ theme }) => theme.colors.mediumGray};
	text-decoration: none;
	color: inherit;
	&:hover {
		border-color: ${({ theme }) => theme.colors.primary};
	}
`

export const TileLogo = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 36px;
	margin-top: 23px;
	img {
		display: block;
	}
`

export const TileLabel = styled.span<{ $twoLines?: boolean }>`
	margin-top: ${({ $twoLines }) => ($twoLines ? '7px' : '16px')};
	font-size: 18px;
	line-height: 21px;
	text-align: center;
	white-space: pre-line;
`

/* Social media feed (Elfsight widget) */
export const SocialFeedHeading = styled(SectionHeading)`
	margin-top: 47px;
`

export const SocialFeedWidget = styled.div`
	margin-top: 21px;
`

/* Mission / Vision band: full-bleed 340px building photo with a 1180×228 dark box inset 50px */
export const HeroQuoteBackground = styled.div`
	position: relative;
	width: 100%;
	height: 340px;
	margin-top: 54px;
	background-size: cover;
	background-position: center;
	background-image: url(${BuildingPhoto});
`

export const QuoteContainer = styled(Container)`
	padding-top: 50px;
`

export const StatementBox = styled.div`
	display: flex;
	justify-content: space-between;
	box-sizing: border-box;
	height: 228px;
	padding: 33px 68px 0;
	background: rgba(51, 51, 51, 0.8);
`

export const Statement = styled.div`
	width: 484px;
	text-align: center;
	color: ${({ theme }) => theme.colors.white};
	h4 {
		margin: 0 0 10px;
		font-size: 33px;
		font-weight: 700;
		line-height: 37px;
		color: ${({ theme }) => theme.colors.primary};
	}
	p {
		margin: 0;
		font-size: 28px;
		font-weight: 400;
		line-height: 34px;
	}
`

/* Core Values: centered 33px heading, three 380px columns, 50px between values */
export const CoreValuesContainer = styled.div`
	margin-top: 44px;
	color: ${({ theme }) => theme.colors.primaryTextColor};
	h3 {
		margin: 0;
		font-size: 33px;
		font-weight: 700;
		line-height: normal;
		text-align: center;
		color: ${({ theme }) => theme.colors.primary};
	}
`

export const CoreValues = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 20px;
	margin-top: 45px;
	margin-bottom: 53px; /* + the content wrapper's own bottom padding = 102px to the footer, as in the Figma */
`

export const CoreValuesColumn = styled.div`
	flex: 0 0 380px;
	display: flex;
	flex-direction: column;
	gap: 50px;
`

export const CoreValue = styled.div`
	h4 {
		margin: 0;
		font-size: 24px;
		font-weight: 700;
		line-height: 37px;
		text-align: center;
		color: ${({ theme }) => theme.colors.primary};
	}
	p {
		margin: 5px 0 0;
		font-size: 16px;
		line-height: 22px;
	}
`
